// src/app/components/dialogs/JudgeDialog.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormHelperText,
  Box,
  Typography,
} from "@material-ui/core";
import Select from "react-select";

export default function JudgeDialog({
  open,
  onClose,
  onSave,
  assignment,       // { _id?, user, gpsDevice, gpsId, index? } | null
  judges = [],      // [{ _id, fullName, email }]
  gpsDevices = [],  // [{ _id, code, phone }]
}) {
  // guardamos el ID (string) para simplificar onSave
  const [user, setUser] = useState("");
  const [gpsDevice, setGpsDevice] = useState("");
  const [gpsId, setGpsId] = useState("");
  const [longitude, setLongitude] = useState("");
  const [latitude, setLatitude] = useState("");

  const isEdit = !!assignment;

  useEffect(() => {
    if (assignment) {
      setUser(
        typeof assignment.user === "object"
          ? assignment.user?._id
          : assignment.user || ""
      );
      setGpsDevice(
        typeof assignment.gpsDevice === "object"
          ? assignment.gpsDevice?._id
          : assignment.gpsDevice || ""
      );
      setGpsId(assignment.gpsId || "");
      setLongitude(assignment.longitude || "");
      setLatitude(assignment.latitude || "");
    } else {
      setUser("");
      setGpsDevice("");
      setGpsId("");
      setLongitude("");
      setLatitude("");
    }
  }, [assignment]);

  const sortedJudges = useMemo(
    () =>
      [...judges].sort((a, b) =>
        (a.fullName || "").localeCompare(b.fullName || "", "es", { sensitivity: "base" })
      ),
    [judges]
  );

  const sortedGps = useMemo(
    () =>
      [...gpsDevices].sort((a, b) =>
        (a.code || "").localeCompare(b.code || "", "es", { sensitivity: "base" })
      ),
    [gpsDevices]
  );

  // opciones para react-select
  const judgeOptions = useMemo(
    () =>
      sortedJudges.map((j) => ({
        value: j._id,
        label: `${j.fullName || "Sin nombre"} (${j.email || "sin email"})`,
      })),
    [sortedJudges]
  );

  const gpsOptions = useMemo(
    () =>
      sortedGps.map((g) => ({
        value: g._id,
        label: `${g.code}${g.phone ? ` (${g.phone})` : ""}`,
      })),
    [sortedGps]
  );

  // Helpers para obtener el objeto option seleccionado a partir del id
  const selectedJudge = useMemo(
    () => judgeOptions.find((o) => o.value === user) || null,
    [judgeOptions, user]
  );

  const selectedGps = useMemo(
    () => gpsOptions.find((o) => o.value === gpsDevice) || null,
    [gpsOptions, gpsDevice]
  );

  const canSave = !!user; // GPS opcional (device o code)

  // Estilos para react-select dentro de MUI Dialog
  const selectStyles = {
    menuPortal: (base) => ({ ...base, zIndex: 1300 }),
    control: (base, state) => ({
      ...base,
      borderColor: state.isFocused ? "#3f51b5" : base.borderColor,
      boxShadow: state.isFocused ? "0 0 0 1px #3f51b5" : base.boxShadow,
      "&:hover": { borderColor: "#3f51b5" },
    }),
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{isEdit ? "Editar juez" : "Añadir juez"}</DialogTitle>
      <DialogContent>
        {/* Juez */}
        <Box mt={1} mb={2}>
          <Typography variant="caption" color="textSecondary">
            Juez
          </Typography>
          <Select
            options={judgeOptions}
            value={selectedJudge}
            onChange={(opt) => setUser(opt ? opt.value : "")}
            placeholder="Selecciona un juez…"
            isClearable
            isDisabled={isEdit} // en edición no cambiamos el juez
            menuPortalTarget={document.body}
            styles={selectStyles}
          />
          {!isEdit && (
            <FormHelperText>
              Busca por nombre o email. Obligatorio.
            </FormHelperText>
          )}
        </Box>

        {/* Dispositivo GPS (opcional) */}
        <Box mt={2} mb={1}>
          <Typography variant="caption" color="textSecondary">
            Dispositivo GPS (opcional)
          </Typography>
          <Select
            options={gpsOptions}
            value={selectedGps}
            onChange={(opt) => {
              setGpsDevice(opt ? opt.value : "");
              if (opt) {
                setGpsId("");
                setLatitude("");
                setLongitude("");
              }
            }}
            placeholder="Sin GPS asignado"
            isClearable
            menuPortalTarget={document.body}
            styles={selectStyles}
          />
          <FormHelperText>
            Si no tienes el dispositivo en catálogo, puedes escribir un código de GPS abajo.
          </FormHelperText>
        </Box>

        {/* Código GPS manual (opcional) */}
        <TextField
          label="Código GPS (opcional)"
          value={gpsId}
          onChange={(e) => {
            const val = e.target.value;
            setGpsId(val);
            if (val) {
              setGpsDevice("");
              setLatitude("");
              setLongitude("");
            }
          }}
          fullWidth
          margin="normal"
          placeholder="p. ej., 34"
        />
        <FormHelperText>
          Déjalo vacío si has seleccionado un dispositivo GPS arriba.
        </FormHelperText>

        <TextField
          label="Latitud (opcional)"
          value={latitude}
          onChange={(e) => {
            const val = e.target.value;
            setLatitude(val);
            if (val) { 
              setGpsDevice("");
              setGpsId("");
            }
          }}
          fullWidth
          margin="normal"
          placeholder="p. ej., 41.40338"
        />

        <TextField
          label="Longitud (opcional)"
          value={longitude}
          onChange={(e) => {
            const val = e.target.value;
            setLongitude(val);
            if (val) {
              setGpsDevice("");
              setGpsId("");
            }
          }}
          fullWidth
          margin="normal"
          placeholder="p. ej., 2.17403"
        />

        <FormHelperText>
          Déjalo vacío si has seleccionado un dispositivo GPS arriba.
        </FormHelperText>

      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button
          onClick={() => {
            const lat = latitude?.trim();
            const lon = longitude?.trim();
            const latNum = lat !== "" && !isNaN(Number(lat)) ? Number(lat) : undefined;
            const lonNum = lon !== "" && !isNaN(Number(lon)) ? Number(lon) : undefined;

            onSave({
              user,
              gpsDevice: gpsDevice || null,
              gpsId: gpsId?.trim() ? gpsId.trim() : undefined,
              latitude: latNum,
              longitude: lonNum,
            });
          }}
          className="btn btn-primary"
          variant="contained"
          disabled={!canSave}
        >
          Guardar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
